import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { Dropdown } from 'react-native-element-dropdown';
import { TextSmallSize } from '../../constants/Fonts';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  totalRecords?: number;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

const LIMIT_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  totalRecords,
  limit,
  onLimitChange,
}) => {
  if (totalRecords === 0) return null;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const renderPageButton = (page: number | string, index: number) => {
    const isEllipsis = typeof page === 'string';
    const isActive = page === currentPage;

    if (isEllipsis) {
      return (
        <View key={`ellipsis-${index}`} style={styles.ellipsisContainer}>
          <Text style={styles.ellipsis}>...</Text>
        </View>
      );
    }

    return (
      <TouchableOpacity
        key={page}
        onPress={() => onPageChange(page as number)}
        disabled={isActive || disabled}
        style={[
          styles.pageButton,
          isActive && styles.activePageButton,
          disabled && styles.disabledButton,
        ]}
      >
        <Text style={[styles.pageText, isActive && styles.activePageText]}>
          {page}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.mainRow}>
        {/* Limit Selector (Left) */}
        <View style={styles.limitSegment}>
          {onLimitChange && (
            <View style={styles.limitContainer}>
              <Text style={styles.limitLabel}>Rows per page:</Text>
              <Dropdown
                style={styles.dropdown}
                selectedTextStyle={styles.dropdownText}
                data={LIMIT_OPTIONS}
                maxHeight={300}
                labelField="label"
                valueField="value"
                value={(limit || 5) as any}
                onChange={item => onLimitChange(item.value)}
                disable={disabled}
              />
            </View>
          )}
        </View>

        {/* Navigation Buttons (Center-ish) - Only show if more than one page */}
        {totalPages > 1 ? (
          <View style={styles.paginationSegment}>
            {/* First Page */}
            <TouchableOpacity
              onPress={() => onPageChange(1)}
              disabled={currentPage === 1 || disabled}
              style={[styles.navButton, (currentPage === 1 || disabled) && styles.disabledButton]}
            >
              <FontAwesome5 name="angle-double-left" size={12} color={currentPage === 1 || disabled ? '#ccc' : '#2e96e1'} />
            </TouchableOpacity>

            {/* Prev Page */}
            <TouchableOpacity
              onPress={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || disabled}
              style={[styles.navButton, (currentPage === 1 || disabled) && styles.disabledButton]}
            >
              <FontAwesome5 name="chevron-left" size={12} color={currentPage === 1 || disabled ? '#ccc' : '#2e96e1'} />
            </TouchableOpacity>

            {/* Page Numbers */}
            <View style={styles.pagesRow}>
              {getPageNumbers().map((page, index) => renderPageButton(page, index))}
            </View>

            {/* Next Page */}
            <TouchableOpacity
              onPress={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || disabled}
              style={[styles.navButton, (currentPage === totalPages || disabled) && styles.disabledButton]}
            >
              <FontAwesome5 name="chevron-right" size={12} color={currentPage === totalPages || disabled ? '#ccc' : '#2e96e1'} />
            </TouchableOpacity>

            {/* Last Page */}
            <TouchableOpacity
              onPress={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages || disabled}
              style={[styles.navButton, (currentPage === totalPages || disabled) && styles.disabledButton]}
            >
              <FontAwesome5 name="angle-double-right" size={12} color={currentPage === totalPages || disabled ? '#ccc' : '#2e96e1'} />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={[styles.paginationSegment, { flex: 1 }]} /> // Placeholder to maintain spacing
        )}

        {/* Record Info (Now Right) */}
        <View style={styles.infoSegment}>
          {totalRecords !== undefined && (
            <Text style={styles.infoText}>
              Showing <Text style={styles.boldText}>{Math.min((currentPage - 1) * (limit || 5) + 1, totalRecords)}</Text> to{' '}
              <Text style={styles.boldText}>{Math.min(currentPage * (limit || 5), totalRecords)}</Text> of{' '}
              <Text style={styles.boldText}>{totalRecords}</Text>
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    alignItems: 'center',
    width: '100%',
    marginTop: -10, // Offset table margin to join the units
  },
  mainRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    flexWrap: 'wrap',
    columnGap: 24,
    rowGap: 12,
  },
  limitSegment: {
    justifyContent: 'center',
  },
  infoSegment: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  paginationSegment: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    justifyContent: 'center',
  },
  infoText: {
    fontSize: TextSmallSize - 1,
    color: '#868e96',
    fontWeight: '400',
  },
  boldText: {
    fontWeight: '600',
    color: '#495057',
  },
  limitContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  limitLabel: {
    fontSize: TextSmallSize - 1,
    color: '#868e96',
    fontWeight: '500',
  },
  dropdown: {
    width: 65,
    height: 30,
    borderWidth: 1,
    borderColor: '#bfbfbf',
    borderRadius: 6,
    paddingHorizontal: 8,
    backgroundColor: '#fff',
  },
  dropdownText: {
    fontSize: TextSmallSize - 1,
    color: '#212529',
    fontWeight: '600',
  },
  pagesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  navButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#bfbfbf',
  },
  pageButton: {
    minWidth: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    paddingHorizontal: 6,
  },
  activePageButton: {
    backgroundColor: '#2e96e1',
    borderColor: '#2e96e1',
    shadowColor: '#2e96e1',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 3,
  },
  disabledButton: {
    opacity: 0.3,
  },
  pageText: {
    fontSize: 13,
    color: '#495057',
    fontWeight: '600',
  },
  activePageText: {
    color: '#fff',
    fontWeight: '700',
  },
  ellipsisContainer: {
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ellipsis: {
    fontSize: 14,
    color: '#ced4da',
    letterSpacing: 2,
  },
});

export default Pagination;
